import { Col, Tooltip } from 'antd';
import { ColumnGroupType, ColumnType, ColumnsType } from 'antd/lib/table';
import config from 'config';
import { Routes } from 'config/routes';
import { format } from 'date-fns';
import {
  DonationListFilterInput,
  DonationListSort,
  DonationOutput,
  PermissionsEnum,
} from 'gql/types';
import { Link } from 'modules/common/components';
import { LocationFilterDropdown } from 'modules/common/components/LocationFilterDropdown';
import RestrictedLink from 'modules/common/components/RestrictedLink';
import { ISortOption } from 'modules/common/interfaces';
import { createSortOrderFactory } from 'modules/common/utils/sort/createSortOrderFactory';
import DonationDropdownActions from 'modules/donations/components/DonationDropdownActions';
import { bloodTypesFilters } from 'modules/donations/components/DonationsTable/filters';
import OutcomeCell from 'modules/donations/components/OutcomeCell';
import { getTranslation } from 'utils/getTranslation';
import { shortenTextWithEllipsis } from 'utils/shortenTextWithEllipsis';

import { OutcomeFlagFilter, TableColumns } from './enum';

import { OutcomeFilterDropdown } from '../OutcomeFilterDropdown';

const t = getTranslation('donation.DonationsTable');

interface IColumnsProps {
  canAddProducts: boolean;
  productCategories?: Record<number, string>;
  filtersValues?: DonationListFilterInput;
  showSorterTooltip?: boolean;
  showFilters?: boolean;
  sort?: ISortOption<DonationListSort>;
}

export const getColumns = ({
  canAddProducts,
  productCategories,
  filtersValues,
  showSorterTooltip,
  showFilters,
  sort,
}: IColumnsProps): ColumnsType<DonationOutput> => {
  const getDefaultSort = createSortOrderFactory<DonationListSort>(sort);
  const getSorterTooltip = (fieldName: DonationListSort) => {
    if (!showSorterTooltip) {
      return false;
    }

    const currentSort = getDefaultSort(fieldName);

    if (!currentSort || currentSort === 'descend') {
      return { title: t('sortAscending') };
    }

    return { title: t('sortDescending') };
  };

  const productCategoriesFilters = productCategories
    ? Object.entries(productCategories).map(([id, categoryName]) => {
        return {
          text: categoryName,
          value: id,
        };
      })
    : [];

  const columns: ((
    | ColumnGroupType<DonationOutput>
    | ColumnType<DonationOutput>
  ) &
    Record<string, any>)[] = [
    {
      title: t('donationId'),
      align: 'center',
      render: ({ shortId }: DonationOutput) => (
        <Link to={`${Routes.Donations}/${shortId}`}>{shortId}</Link>
      ),
    },
    {
      title: t('donorName'),
      align: 'center',
      key: DonationListSort.DonorName,
      sorter: true,
      sortOrder: getDefaultSort(DonationListSort.DonorName),
      showSorterTooltip,
      render: ({ donor }: DonationOutput) => (
        <RestrictedLink
          to={`${Routes.Donors}/${donor.shortId}`}
          permissions={[PermissionsEnum.ViewTheListOfDonors]}
        >
          {donor.name}
        </RestrictedLink>
      ),
    },
    {
      title: t('ownerLastName'),
      align: 'center',
      sorter: true,
      sortOrder: getDefaultSort(DonationListSort.OwnerLastName),
      showSorterTooltip,
      key: DonationListSort.OwnerLastName,
      render: (row: DonationOutput) => (
        <RestrictedLink
          to={Routes.EditOwner.replace(':id', `${row.donor.user?.shortId}`)}
          permissions={[PermissionsEnum.ViewTheListOfOwners]}
        >
          {row.donor.user?.lastName || ''}
        </RestrictedLink>
      ),
    },
    {
      title: t('bloodType'),
      align: 'center',
      key: TableColumns.bloodTypes,
      render: (row: DonationOutput) => (
        <>{row.donor.bloodType && t(row.donor.bloodType?.toLowerCase())}</>
      ),
      filters: bloodTypesFilters,
      filteredValue: filtersValues?.bloodTypes || null,
    },
    {
      title: t('outcome'),
      align: 'center',
      key: TableColumns.outcome,
      filteredValue: [
        ...(filtersValues?.productCategories?.map(String) || []),
        ...(filtersValues?.ineligible ? [OutcomeFlagFilter.Ineligible] : []),
        ...(filtersValues?.failed ? [OutcomeFlagFilter.Failed] : []),
        ...(filtersValues?.discarded ? [OutcomeFlagFilter.Discarded] : []),
      ],
      filterDropdown: showFilters
        ? (props) => (
            <OutcomeFilterDropdown
              {...props}
              productCategoriesFilters={productCategoriesFilters}
            />
          )
        : undefined,
      render: (row: DonationOutput) => {
        return (
          <OutcomeCell donation={row} productCategories={productCategories} />
        );
      },
    },
    {
      title: t('location'),
      key: TableColumns.locations,
      filteredValue: filtersValues?.locations || null,
      filterDropdown: showFilters
        ? (props) => <LocationFilterDropdown {...props} />
        : undefined,
      render: (row: DonationOutput) => {
        return row.donor.location ? (
          <Tooltip title={row.donor.location}>
            {shortenTextWithEllipsis(row.donor.location, 30)}
          </Tooltip>
        ) : (
          '-'
        );
      },
      align: 'center',
    },
    {
      title: t('dateOfLastDonation'),
      align: 'center',
      key: DonationListSort.LastDonationDate,
      sorter: true,
      sortOrder: getDefaultSort(DonationListSort.LastDonationDate),
      showSorterTooltip: getSorterTooltip(DonationListSort.LastDonationDate),
      sortDirections: ['ascend', 'descend'],
      render: (row: DonationOutput) => (
        <Col>
          {format(new Date(row.donationDate), config.DATE_FORMATS.DATE)}
        </Col>
      ),
    },
    {
      render: (row: DonationOutput) => (
        <DonationDropdownActions donation={row} />
      ),
      filter: !canAddProducts,
    },
  ];

  return columns.reduce((previousValue, currentValue) => {
    const { filter, ...columnData } = currentValue;
    if (currentValue.filter) {
      return previousValue;
    }
    return [...previousValue, columnData as any];
  }, [] as ColumnsType<DonationOutput>);
};
